import { useEffect, useState } from 'react';
import { listReports } from '../services/report.js';
import { useAppAuth } from '../components/AuthContext.jsx';
import { getJrSummary } from '../services/jrOverview.js';
import { usePolicy } from '../components/PolicyContext.jsx';

// Týdenní report (JCH-Report). One row per makler for the last closed week:
// phase, plan vs. completion, semafor (only from mandatory contacts, vs. own
// plan), and the number of weeks spent in red. Built on the JCH-only /reports
// route.
//
// Backend contract: GET /reports → { reports, count }. Each report row exposes
// makler_id, makler_name, makler_email, report_type, generated_at, and the
// delivery fields (email_status, push_notification_sent).
//
// The report model only stores per-makler totals and delivery metadata — the
// closed-week semafor / plan / red-weeks columns aren't surfaced by this
// route, so those cells render as „—" (the numbers live in the makler detail
// / Společná čísla, which reads the open weekly plan).
//
// deliveryChip shows how the report was delivered by e-mail (or that it is
// still pending / a send failed). Reports only carry delivery metadata.
function deliveryChip(r) {
  const delivery = r.email_status || 'pending';
  if (delivery === 'sent') return <span className="semafor semafor--grn">Odeslán</span>;
  if (delivery === 'failed') return <span className="semafor semafor--red">Selhalo</span>;
  return <span className="semafor semafor--org">Čeká</span>;
}

function semaforChip(c) {
  if (c === 'green') return <span className="semafor semafor--grn">Zelená</span>;
  if (c === 'orange') return <span className="semafor semafor--org">Oranžová</span>;
  if (c === 'red') return <span className="semafor semafor--red">Červená</span>;
  return '—';
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function Report() {
  const { policy } = usePolicy();
  const { user } = useAppAuth();
  const [reports, setReports] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [sum, rep] = await Promise.all([
        getJrSummary(),
        user?.role === 'JCH' ? listReports().catch(() => ({ reports: [] })) : { reports: [] },
      ]);
      setRows(sum.rows || sum.maklers || []);
      setReports(rep.reports || []);
    } catch (err) {
      setError(err.message || 'Reporty se nepodařilo načíst.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Týdenní report</h1>
          <p className="dashboard__lead">
            Týdenní report z uzavřeného týdne ti navíc chodí každý čtvrtek
            e-mailem a na Disk. JR s ním nemá práci a Drill ho vygeneruje
            automaticky za minulý uzavřený týden.
          </p>
        </div>
        <div className="dashboard__status">
          {rows.length} makléřů
        </div>
      </div>

      {error && (
        <p className="dashboard__error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="dashboard__status">Načítá se…</p>
      ) : rows.length === 0 ? (
        <article className="card">
          <p className="card__text">Zatím žádní makléři.</p>
        </article>
      ) : (
        <div className="report">
          <div className="report__row report__row--head">
            <span className="report__head">Makléř</span>
            <span className="report__head">Úroveň</span>
            <span className="report__head">Plán / plnění (týden)</span>
            <span className="report__head">Minulý týden</span>
            <span className="report__head">Barva</span>
            <span className="report__head">Odeslaný report</span>
            <span className="report__head">Poznámka</span>
          </div>
          {rows.map((m) => {
            const sent = reports.find((r) => r.makler_id === m.id);
            const cw = m.current_week;
            return (
              <div key={m.id} className="report__row">
                <span className="report__cell report__cell--name">{m.name}</span>
                <span className="report__cell" style={{ color: m.level_color }}>{m.level || '—'}</span>
                <span className="report__cell">{cw ? `${cw.done} / ${cw.plan}` : '—'}</span>
                <span className="report__cell">
                  {m.semafor_pct != null ? `${Math.round(m.semafor_pct)} %` : '—'}
                </span>
                <span className="report__cell">{semaforChip(m.semafor)}</span>
                <span className="report__cell">{sent ? deliveryChip(sent) : '—'}</span>
                <span className="report__cell report__cell--note">
                  {m.semafor === 'red' ? 'Minulý týden v červené – ozvi se.' : sent ? noteText(sent) : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <p className="report__note">
        Barva jen z povinných oslovení: zelená od {policy.trafficLight.greenFrom} %,
        oranžová od {policy.trafficLight.orangeFrom} % vlastního plánu.
        Nulový plán je bez barvy. Zobrazení používá aktuální hranice; odeslané reporty se nemění.
      </p>
    </section>
  );
}

function phaseLabel(r) {
  // reports carry the makler's phase via the joined makler row; fall back to
  // the report type when it isn't present.
  return r.phase || r.report_type || '—';
}

function noteText(r) {
  const delivery = r.email_status || 'pending';
  if (delivery === 'sent') return `Odeslán · ${fmtDate(r.updated_at)}`;
  if (delivery === 'failed') return 'Odesílání selhalo';
  return 'Čeká odeslání';
}

// 1 / 3 / 5 reports — Czech counts.
// eslint-disable-next-line no-unused-vars
function pluralReports(n) {
  if (n === 1) return 'report';
  const last = n % 10;
  const ten = n % 100;
  if (ten >= 11 && ten <= 19) return 'reportů';
  if (last >= 2 && last <= 4) return 'reporty';
  return 'report';
}
